import UIComponent from "sap/ui/core/UIComponent";
import models from "./model/models";
import Device from "sap/ui/Device";
import AuthService from "./service/AuthService";
import LanguageService from "./service/LanguageService";
import SettingsService from "./service/SettingsService";
import type { BrandingInfo } from "./service/SettingsService";
import HashChanger from "sap/ui/core/routing/HashChanger";
import JSONModel from "sap/ui/model/json/JSONModel";
import Theming from "sap/ui/core/Theming";
import type { AngThemeResolved } from "./theme/AngTheme";

/**
 * @namespace com.ang.portal
 */
export default class Component extends UIComponent {
	public static metadata = {
		manifest: "json",
		interfaces: ["sap.ui.core.IAsyncContentCreation"],
	};

	private contentDensityClass: string;

	public init(): void {
		// call the base component's init function
		super.init();

		// create the device model
		this.setModel(models.createDeviceModel(), "device");

		// create settings model with defaults and load branding from backend.
		// Branding (title, logo and login-page copy) is public so it works on the
		// login page and for every authenticated user regardless of role.
		const oSettingsModel = new JSONModel({
			title: "Portal",
			logoUrl: "",
			loginWelcomeTitle: "",
			loginWelcomeText: "",
			loginFooterText: "",
			// Assume on until branding says otherwise, so a failed load does not
			// hide a working sign-in option.
			ssoEnabled: true,
		});
		this.setModel(oSettingsModel, "settings");
		void SettingsService.getBranding()
			.then((data) => {
				this.applyBranding(oSettingsModel, data);
				return this.applyTheme(data.themeInstance);
			})
			.catch(() => {
				// Keep defaults on error
			});

		// attach route guard before initializing router
		const oRouter = this.getRouter();
		oRouter.attachRouteMatched(() => this.onRouteGuard(), this);

		// create the views based on the url/hash
		oRouter.initialize();
	}

	private settingsLoaded = false;

	/** The theme last applied, re-applied after a UI5 stylesheet swap. */
	private appliedTheme: AngThemeResolved | null = null;
	private themeAppliedAttached = false;

	/**
	 * Copy the public branding payload into the "settings" model. Empty login
	 * texts are kept as empty strings so the Login view falls back to i18n.
	 */
	private applyBranding(oSettingsModel: JSONModel, data: BrandingInfo): void {
		oSettingsModel.setProperty("/title", data.title || "Portal");
		oSettingsModel.setProperty("/logoUrl", data.logoUrl || "");
		oSettingsModel.setProperty(
			"/loginWelcomeTitle",
			data.loginWelcomeTitle || "",
		);
		oSettingsModel.setProperty("/loginWelcomeText", data.loginWelcomeText || "");
		oSettingsModel.setProperty("/loginFooterText", data.loginFooterText || "");
		oSettingsModel.setProperty("/ssoEnabled", data.ssoEnabled !== false);
	}

	/**
	 * Apply the company's colour instance.
	 *
	 * Public so the Settings page can re-apply after an admin picks a different
	 * one, without a reload: the variables are inline styles, so swapping them
	 * repaints immediately.
	 *
	 * An empty id means "whatever the JSON calls default" - the loader handles
	 * that, and also falls back to the default for an id it does not recognise,
	 * so a stale value in the database costs the default appearance rather than
	 * an unstyled portal.
	 */
	public async applyTheme(instance?: string): Promise<void> {
		// AngTheme comes from theme/theme-loader.js, loaded by a <script> tag in
		// index.html AND index-cdn.html. Named explicitly because forgetting one
		// of those tags otherwise surfaces as a bare ReferenceError inside the
		// catch below, which says nothing about the cause - it cost an hour once.
		if (typeof AngTheme === "undefined") {
			console.error(
				"Theme loader missing: add <script src=\"theme/theme-loader.js\"> " +
					"to webapp/index.html and webapp/index-cdn.html.",
			);
			return;
		}

		try {
			const theme = await AngTheme.load(
				sap.ui.require.toUrl("com/ang/portal/theme/theme-instances.json"),
				instance ? { instance } : {},
			);

			if (theme.ui5Theme && theme.ui5Theme !== Theming.getTheme()) {
				Theming.setTheme(theme.ui5Theme);
			}

			// Cache for theme-bootstrap.js, so the next load of a dark instance
			// boots dark instead of booting light and flipping. Only the theme
			// NAME is cached - the colours are re-read from the JSON every time,
			// so editing that file is never masked by a stale cache.
			try {
				window.localStorage.setItem("portal.ui5Theme", theme.ui5Theme);
			} catch {
				// Storage blocked - the flash on next load is the only cost.
			}

			// UI5 replaces its stylesheet <link> on a theme switch. The variables
			// are inline styles on <html> and outrank any stylesheet, so they
			// survive - but re-applying costs nothing and removes the question.
			//
			// Attached ONCE and reading the latest theme: attaching per call left
			// one handler per Settings preview, each holding an older theme, all
			// firing in turn on the next UI5 theme switch.
			this.appliedTheme = theme;
			if (!this.themeAppliedAttached) {
				this.themeAppliedAttached = true;
				Theming.attachApplied(() => {
					if (this.appliedTheme) {
						AngTheme.apply(this.appliedTheme);
					}
				});
			}
		} catch (error) {
			// A missing or malformed theme file must not stop the portal from
			// rendering; it falls back to stock Horizon.
			console.error("Theme could not be applied:", error);
		}
	}

	private onRouteGuard(): void {
		const sHash = HashChanger.getInstance().getHash();
		const bAuthenticated = AuthService.isAuthenticated();
		const bPublicResetRoute = sHash.startsWith("reset-password");

		if (bAuthenticated) {
			// Adopt the language stored on the account when this browser has a
			// different one (or none yet) — e.g. the user's first login here. This
			// reloads the page, so stop processing the route.
			if (LanguageService.syncFromUser(AuthService.getUserInfo()?.language)) {
				return;
			}
		}

		if (!bAuthenticated && sHash !== "" && !bPublicResetRoute) {
			// Not authenticated and not on login page → redirect to login
			this.getRouter().navTo("login", {}, undefined, true);
		} else if (bAuthenticated && !this.settingsLoaded) {
			// Refresh branding once after authentication in case it changed.
			const oSettingsModel = this.getModel("settings") as JSONModel;
			void SettingsService.getBranding()
				.then((data) => {
					this.applyBranding(oSettingsModel, data);
					this.settingsLoaded = true;
				})
				.catch(() => {
					// Keep defaults on error; allow retry on next route change
				});
		}
	}

	/**
	 * This method can be called to determine whether the sapUiSizeCompact or sapUiSizeCozy
	 * design mode class should be set, which influences the size appearance of some controls.
	 * @public
	 * @returns css class, either 'sapUiSizeCompact' or 'sapUiSizeCozy' - or an empty string if no css class should be set
	 */
	public getContentDensityClass(): string {
		if (this.contentDensityClass === undefined) {
			// check whether FLP has already set the content density class; do nothing in this case
			if (
				document.body.classList.contains("sapUiSizeCozy") ||
				document.body.classList.contains("sapUiSizeCompact")
			) {
				this.contentDensityClass = "";
			} else if (!Device.support.touch) {
				// apply "compact" mode if touch is not supported
				this.contentDensityClass = "sapUiSizeCompact";
			} else {
				// "cozy" in case of touch support; default for most sap.m controls, but needed for desktop-first controls like sap.ui.table.Table
				this.contentDensityClass = "sapUiSizeCozy";
			}
		}
		return this.contentDensityClass;
	}
}
